// The Twetch device session key, and the signatures it produces.
//
// A Twetch account rooted in a BTC wallet has TWO keys with different jobs:
//
//  * The WALLET key — the user's own, never seen here. It signs BIP-322
//    challenges (register/login) and the per-post AIP domain, one explicit
//    popup at a time.
//  * The DEVICE key — a throwaway secp256k1 keypair this file generates in
//    the browser. It is a SESSION credential: registered as `devicePubkey`,
//    it signs the transport headers on every write so the wallet doesn't
//    have to popup for plumbing. It is NOT an identity, NOT a backup, and is
//    never displayed; a wallet re-login mints and registers a fresh one
//    (the server keeps one device per account).
//
// Transport signatures are Bitcoin-Signed-Message (the legacy P2PKH magic —
// what Twetch's server verifies against the registered device pubkey via
// public-key recovery), produced here with @noble/secp256k1 over the
// double-SHA256 of the magic-framed message.

import * as secp from '@noble/secp256k1'

export interface DeviceKey {
	/// 32-byte private scalar, hex. Lives in localStorage only.
	priv: string
	/// Compressed public key, 66 hex chars — the registered `devicePubkey`.
	pub: string
}

const DEVICE_KEY_STORAGE = 'alkanes-twetch-device-v1'

export function newDeviceKey(): DeviceKey {
	const priv = secp.utils.randomPrivateKey()
	return {
		priv: bytesToHex(priv),
		pub: bytesToHex(secp.getPublicKey(priv, true))
	}
}

export function loadDeviceKey(): DeviceKey | null {
	try {
		const raw = window.localStorage.getItem(DEVICE_KEY_STORAGE)
		if (!raw) {
			return null
		}
		const parsed = JSON.parse(raw) as Partial<DeviceKey>
		if (
			typeof parsed.priv === 'string' &&
			/^[0-9a-f]{64}$/.test(parsed.priv) &&
			typeof parsed.pub === 'string' &&
			/^[0-9a-f]{66}$/.test(parsed.pub)
		) {
			return { priv: parsed.priv, pub: parsed.pub }
		}
		return null
	} catch {
		return null
	}
}

export function storeDeviceKey(key: DeviceKey): void {
	try {
		window.localStorage.setItem(DEVICE_KEY_STORAGE, JSON.stringify(key))
	} catch {
		// Blocked storage — the in-memory key still signs for this visit.
	}
}

export function clearDeviceKey(): void {
	try {
		window.localStorage.removeItem(DEVICE_KEY_STORAGE)
	} catch {
		// nothing to clear
	}
}

/// 32 hex chars (128 bits) of client entropy — the server's nonce minimum.
export function randomHexNonce(): string {
	const bytes = new Uint8Array(16)
	crypto.getRandomValues(bytes)
	return bytesToHex(bytes)
}

export function bytesToHex(bytes: Uint8Array): string {
	let s = ''
	for (const b of bytes) {
		s += b.toString(16).padStart(2, '0')
	}
	return s
}

export function hexToBytes(hex: string): Uint8Array {
	if (hex.length % 2 !== 0 || /[^0-9a-fA-F]/.test(hex)) {
		throw new Error('invalid hex')
	}
	const out = new Uint8Array(hex.length / 2)
	for (let i = 0; i < out.length; i++) {
		out[i] = parseInt(hex.slice(i * 2, i * 2 + 2), 16)
	}
	return out
}

/// Strict UTF-8 decode: null when the bytes are not valid UTF-8. Used before
/// handing bytes to a wallet that signs STRINGS — valid UTF-8 has a unique
/// encoding, so decode → sign → the wallet's re-encode is byte-identical; if
/// this fails the caller must refuse to sign rather than let the wallet sign
/// different bytes than the server verifies.
export function utf8DecodeStrict(bytes: Uint8Array): string | null {
	try {
		return new TextDecoder('utf-8', { fatal: true }).decode(bytes)
	} catch {
		return null
	}
}

// ── Bitcoin Signed Message (legacy magic) ──────────────────────────────────

const BSM_MAGIC = 'Bitcoin Signed Message:\n'

function varint(n: number): Uint8Array {
	if (n < 0xfd) {
		return Uint8Array.from([n])
	}
	if (n <= 0xffff) {
		return Uint8Array.from([0xfd, n & 0xff, n >> 8])
	}
	// Transport messages are canonical request strings — bounded far below
	// 4 GiB; 32-bit covers every reachable size.
	return Uint8Array.from([
		0xfe,
		n & 0xff,
		(n >> 8) & 0xff,
		(n >> 16) & 0xff,
		(n >>> 24) & 0xff
	])
}

function concatBytes(...parts: Uint8Array[]): Uint8Array {
	const out = new Uint8Array(parts.reduce((sum, p) => sum + p.length, 0))
	let offset = 0
	for (const p of parts) {
		out.set(p, offset)
		offset += p.length
	}
	return out
}

/// The double-SHA256 digest BSM signs: sha256d(varint(|magic|) || magic ||
/// varint(|msg|) || msg). Exported so tests can verify signatures by
/// recovering the public key over the same digest.
export async function bsmDigest(message: string): Promise<Uint8Array> {
	const enc = new TextEncoder()
	const magic = enc.encode(BSM_MAGIC)
	const msg = enc.encode(message)
	const framed = concatBytes(varint(magic.length), magic, varint(msg.length), msg)
	return secp.utils.sha256(await secp.utils.sha256(framed))
}

/// Sign `message` with the device key → base64 65-byte compact signature
/// (header 31–34: compressed-key recovery), the exact format the server's
/// BSM verifier recovers a pubkey from.
export async function bsmSign(privHex: string, message: string): Promise<string> {
	const digest = await bsmDigest(message)
	const [sig, recovery] = await secp.sign(digest, privHex, { recovered: true, der: false })
	const compact = new Uint8Array(65)
	compact[0] = 27 + recovery + 4 // +4: the signing key is compressed
	compact.set(sig, 1)
	let binary = ''
	for (const b of compact) {
		binary += String.fromCharCode(b)
	}
	return btoa(binary)
}
